import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {import('./slideshow').Slideshow} slideshow - The slideshow component.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  /** @type {HTMLElement | null} */
  #activeZoomMedia = null;

  connectedCallback() {
    super.connectedCallback();
    this.refs.slideshow?.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.addEventListener('pointerdown', this.#handleMediaPointerDown, true);
    this.addEventListener('pointermove', this.#handleMediaPointerMove, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.slideshow?.removeEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    this.removeEventListener('pointerdown', this.#handleMediaPointerDown, true);
    this.removeEventListener('pointermove', this.#handleMediaPointerMove, true);
  }

  /**
   * Enables in-place zoom while pointer is held down on a zoomable media item.
   * @param {PointerEvent} event
   */
  #handleMediaPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target.closest('.zoom-dialog__close, .slideshow-control, button, a, video, iframe, model-viewer')) return;

    const media = target.closest('.zoom-dialog__media, .zoom-dialog__stacked-item');
    if (!(media instanceof HTMLElement)) return;

    const image = media.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    event.preventDefault();
    event.stopPropagation();

    if (media.classList.contains('zoom-dialog__media--zoomed')) {
      this.#resetInPlaceZoom();
      return;
    }

    this.#resetInPlaceZoom();
    this.#activeZoomMedia = media;

    this.#setZoomOriginFromPoint(media, event.clientX, event.clientY);
    media.classList.add('zoom-dialog__media--zoomed');
  };

  /**
   * While pointer is down, update zoom focal point to follow cursor/finger.
   * @param {PointerEvent} event
   */
  #handleMediaPointerMove = (event) => {
    if (event.pointerType !== 'mouse') return;
    const media = this.#activeZoomMedia;
    if (!(media instanceof HTMLElement)) return;
    if (!media.classList.contains('zoom-dialog__media--zoomed')) return;

    this.#setZoomOriginFromPoint(media, event.clientX, event.clientY);
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} [event] - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault?.();

    if (window.matchMedia('(max-width: 989px)').matches) {
      event?.stopPropagation?.();
      return;
    }

    const { dialog } = this.refs;
    const slides = this.#slides;
    const targetSlide = slides?.[index];
    const stackedItems = this.#stackedItems;
    const targetStackedItem = stackedItems?.[index];

    const openDialog = () => {
      dialog.showModal();

      if (this.refs.slideshow) {
        this.refs.slideshow.select(index, event, { animate: false });
      } else if (targetStackedItem) {
        targetStackedItem.scrollIntoView({ behavior: 'instant', block: 'start' });
      } else {
        targetSlide?.scrollIntoView({ behavior: 'instant' });
      }

      trapFocus(dialog);

      const closeBtn = this.querySelector('.zoom-dialog__close');
      if (closeBtn instanceof HTMLElement) {
        closeBtn.focus({ preventScroll: true });
      } else {
        dialog.focus();
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetSlide) return openDialog();

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      openDialog();
      sourceImage.style.removeProperty('view-transition-name');
      targetSlide.style.setProperty('view-transition-name', transitionName);
    });

    targetSlide.style.removeProperty('view-transition-name');
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog } = this.refs;
    this.#resetInPlaceZoom();

    if (!supportsViewTransitions() || isLowPowerDevice() || !this.refs.slideshow) return this.closeDialog();

    const activeIndex = this.refs.slideshow?.current ?? 0;
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';
    const slide = slideshowActive ? mediaGallery?.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(dialog);

    const targetSlide = this.#slides?.[activeIndex];
    if (targetSlide) {
      targetSlide.style.setProperty('view-transition-name', transitionName);
    }

    await startViewTransition(() => {
      targetSlide?.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    this.#resetInPlaceZoom();
    dialog.close();
    removeTrapFocus();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * @param {HTMLElement} media
   * @param {number} clientX
   * @param {number} clientY
   */
  #setZoomOriginFromPoint(media, clientX, clientY) {
    const image = media.querySelector('img');
    if (!(image instanceof HTMLImageElement)) return;

    const rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const originX = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const originY = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));

    media.style.setProperty('--zoom-origin-x', `${originX}%`);
    media.style.setProperty('--zoom-origin-y', `${originY}%`);
  }

  #resetInPlaceZoom() {
    const zoomedItems = this.querySelectorAll('.zoom-dialog__media--zoomed');
    zoomedItems.forEach((media) => {
      if (!(media instanceof HTMLElement)) return;
      media.classList.remove('zoom-dialog__media--zoomed');
      media.style.removeProperty('--zoom-origin-x');
      media.style.removeProperty('--zoom-origin-y');
    });

    this.#activeZoomMedia = null;
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  #handleSlideshowSelect = (event) => {
    this.#resetInPlaceZoom();
    const index = event?.detail?.index;
    if (typeof index !== 'number') return;
    const slide = this.#slides?.[index];
    if (slide) this.#loadHighResolutionImage(slide);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  };

  get #slides() {
    return this.refs.slideshow?.refs?.slides;
  }

  get #stackedItems() {
    return this.refs.stackedItems;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}